// DESCRIPTION: Frontend events display entry point. Mounts the EventsDisplay
// React app on .nb-events-display containers rendered by the [nemtly_event_list] shortcode.

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { __ } from '@wordpress/i18n';
import { applyFilters } from '@wordpress/hooks';

import '../../css/events-frontend.css';

import EventCard from './components/EventCard';
import EventListItem from './components/EventListItem';
import LayoutToggle from './components/LayoutToggle';
import SearchInput from './components/SearchInput';
import EventSkeleton from './components/EventSkeleton';
import ErrorState from './components/ErrorState';

/**
 * Filter Buttons Component
 * @param root0
 * @param root0.activeFilter
 * @param root0.onFilterChange
 */
const FilterButtons = ( { activeFilter, onFilterChange } ) => {
	const filters = [
		{ key: 'all', label: __( 'All', 'nemtly-booking' ) },
		{ key: 'live', label: __( 'Live', 'nemtly-booking' ) },
		{ key: 'upcoming', label: __( 'Upcoming', 'nemtly-booking' ) },
	];

	return (
		<div className="nb-mb-6">
			<div className="nb-inline-flex nb-gap-1 nb-p-1 nb-rounded-lg nb-bg-gray-100">
				{ filters.map( ( filter ) => (
					<button
						key={ filter.key }
						className={ `nb-px-3.5 nb-py-1.5 nb-rounded-md nb-text-sm nb-font-medium nb-transition-all nb-duration-150 focus:nb-outline-none ${
							activeFilter === filter.key
								? 'nb-bg-white nb-text-gray-900 nb-shadow-sm'
								: 'nb-text-gray-500 hover:nb-text-gray-700'
						}` }
						onClick={ () => onFilterChange( filter.key ) }
						data-filter={ filter.key }
					>
						{ filter.label }
					</button>
				) ) }
			</div>
		</div>
	);
};

/**
 * Events Display Component
 * @param root0
 * @param root0.limit
 * @param root0.showFilters
 * @param root0.showPagination
 * @param root0.showSearch
 * @param root0.initialLayout
 */
const EventsDisplay = ( {
	limit = 12,
	showFilters = true,
	showPagination = true,
	showSearch = false,
	initialLayout = 'grid',
} ) => {
	const [ events, setEvents ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ currentPage, setCurrentPage ] = useState( 1 );
	const [ totalPages, setTotalPages ] = useState( 1 );
	const [ activeFilter, setActiveFilter ] = useState( 'all' );
	const [ layout, setLayout ] = useState( initialLayout );
	const [ searchTerm, setSearchTerm ] = useState( '' );
	const [ error, setError ] = useState( null );

	// Track whether we've loaded at least once (for skeleton vs inline loading)
	const hasLoadedOnce = useRef( false );

	const loadEvents = async (
		filter = activeFilter,
		page = 1,
		search = searchTerm
	) => {
		setLoading( true );
		setError( null );

		try {
			const response = await fetch(
				'/wp-json/nemtly-booking/v1/events/frontend',
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-WP-Nonce': window.nemtlyEventsAjax.restNonce,
					},
					body: JSON.stringify( {
						filter,
						page,
						limit,
						search,
					} ),
				}
			);

			const data = await response.json();

			if ( data.success ) {
				setEvents( data.data.events );
				setTotalPages( data.data.pagination.total_pages );
				setCurrentPage( data.data.pagination.current_page );
				hasLoadedOnce.current = true;
			} else {
				setError(
					data.message ||
						__( 'Failed to load events.', 'nemtly-booking' )
				);
			}
		} catch ( err ) {
			setError(
				__(
					'Failed to load events. Please try again.',
					'nemtly-booking'
				)
			);
		} finally {
			setLoading( false );
		}
	};

	const handleFilterChange = ( filter ) => {
		setActiveFilter( filter );
		setCurrentPage( 1 );
		loadEvents( filter, 1, searchTerm );
	};

	const handlePageChange = ( page ) => {
		setCurrentPage( page );
		loadEvents( activeFilter, page, searchTerm );
	};

	const handleSearch = ( term ) => {
		setSearchTerm( term );
		setCurrentPage( 1 );
		loadEvents( activeFilter, 1, term );
	};

	const handleViewEvent = ( eventId, eventSlug ) => {
		const currentUrl = window.location.origin;
		window.location.href = `${ currentUrl }/events/${ eventSlug }/`;
	};

	useEffect( () => {
		loadEvents();
	}, [] );

	// Determine content to render
	const renderContent = () => {
		// Initial load — show skeleton
		if ( loading && ! hasLoadedOnce.current ) {
			return <EventSkeleton layout={ layout } />;
		}

		// Error state
		if ( error ) {
			return (
				<ErrorState message={ error } onRetry={ () => loadEvents() } />
			);
		}

		// Empty state
		if ( ! loading && events.length === 0 ) {
			return (
				<div className="nb-text-center nb-py-12 nb-text-gray-600">
					<p>{ __( 'No events found.', 'nemtly-booking' ) }</p>
				</div>
			);
		}

		// Events loaded — render with optional loading overlay for subsequent fetches
		const contentClass = loading
			? 'nb-opacity-50 nb-pointer-events-none nb-transition-opacity nb-duration-200'
			: 'nb-transition-opacity nb-duration-200';

		if ( layout === 'grid' ) {
			return (
				<div className={ contentClass }>
					<div className="nb-grid nb-grid-cols-1 sm:nb-grid-cols-2 nb-gap-6">
						{ events.map( ( event ) => {
							const ctx = { event, layout };

							/**
							 * Filters the props passed to <EventCard>.
							 *
							 * @param {Object} props { event }.
							 * @param {Object} ctx   { event, layout }.
							 */
							const cardProps = applyFilters(
								'nemtly_booking.events.card_props',
								{ event },
								ctx
							);

							/**
							 * Filters the rendered event card element.
							 *
							 * @param {Element} element The <EventCard> element.
							 * @param {Object}  ctx     { event, layout }.
							 */
							return applyFilters(
								'nemtly_booking.events.card',
								<EventCard key={ event.id } { ...cardProps } />,
								ctx
							);
						} ) }
					</div>
				</div>
			);
		}

		return (
			<div className={ contentClass }>
				<ul role="list" className="nb-divide-y nb-divide-gray-200">
					{ events.map( ( event ) => {
						const ctx = { event, layout };

						/**
						 * Filters the props passed to <EventListItem>.
						 *
						 * @param {Object} props { event, onViewEvent }.
						 * @param {Object} ctx   { event, layout }.
						 */
						const itemProps = applyFilters(
							'nemtly_booking.events.list_item_props',
							{ event, onViewEvent: handleViewEvent },
							ctx
						);

						/**
						 * Filters the rendered event list item element.
						 *
						 * @param {Element} element The <EventListItem> element.
						 * @param {Object}  ctx     { event, layout }.
						 */
						return applyFilters(
							'nemtly_booking.events.list_item',
							<EventListItem key={ event.id } { ...itemProps } />,
							ctx
						);
					} ) }
				</ul>
			</div>
		);
	};

	return (
		<div className="nb-events-display">
			{ /* Filter Buttons */ }
			{ showFilters && (
				<FilterButtons
					activeFilter={ activeFilter }
					onFilterChange={ handleFilterChange }
				/>
			) }

			{ /* Toolbar: Search + Layout Toggle */ }
			<div className="nb-flex nb-items-center nb-justify-between nb-gap-4 nb-mb-6">
				{ showSearch ? (
					<SearchInput onSearch={ handleSearch } />
				) : (
					<div />
				) }
				<LayoutToggle layout={ layout } onLayoutChange={ setLayout } />
			</div>

			{ /* Events Content */ }
			{ renderContent() }

			{ /* Pagination */ }
			{ showPagination && totalPages > 1 && ! loading && (
				<Pagination
					currentPage={ currentPage }
					totalPages={ totalPages }
					onPageChange={ handlePageChange }
				/>
			) }
		</div>
	);
};

/**
 * Pagination Component
 * @param root0
 * @param root0.currentPage
 * @param root0.totalPages
 * @param root0.onPageChange
 */
const Pagination = ( { currentPage, totalPages, onPageChange } ) => {
	const pages = [];
	const maxVisible = 7;

	let startPage = Math.max( 1, currentPage - Math.floor( maxVisible / 2 ) );
	const endPage = Math.min( totalPages, startPage + maxVisible - 1 );

	if ( endPage - startPage + 1 < maxVisible ) {
		startPage = Math.max( 1, endPage - maxVisible + 1 );
	}

	for ( let i = startPage; i <= endPage; i++ ) {
		pages.push( i );
	}

	return (
		<div className="nb-flex nb-items-center nb-justify-center nb-gap-1 nb-mt-12 nb-mb-8">
			{ /* Previous button */ }
			{ currentPage > 1 && (
				<button
					className="nb-flex nb-items-center nb-gap-2 nb-px-4 nb-py-2.5 nb-rounded-lg nb-border nb-border-slate-200 nb-bg-white nb-text-slate-700 nb-font-medium hover:nb-bg-slate-50 hover:nb-border-slate-300 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-slate-500 focus:nb-ring-offset-2 nb-transition-all nb-duration-200 nb-shadow-sm"
					onClick={ () => onPageChange( currentPage - 1 ) }
					aria-label={ __( 'Go to previous page', 'nemtly-booking' ) }
				>
					<svg
						className="nb-w-4 nb-h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={ 2 }
							d="M15 19l-7-7 7-7"
						/>
					</svg>
					<span className="nb-hidden sm:nb-inline">
						{ __( 'Previous', 'nemtly-booking' ) }
					</span>
				</button>
			) }

			{ /* First page */ }
			{ startPage > 1 && (
				<>
					<button
						className="nb-px-3 nb-py-2.5 nb-rounded-lg nb-border nb-border-slate-200 nb-bg-white nb-text-slate-700 nb-font-medium hover:nb-bg-slate-50 hover:nb-border-slate-300 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-slate-500 focus:nb-ring-offset-2 nb-transition-all nb-duration-200 nb-shadow-sm"
						onClick={ () => onPageChange( 1 ) }
					>
						1
					</button>
					{ startPage > 2 && (
						<span className="nb-px-2 nb-py-2.5 nb-text-slate-400 nb-font-medium">
							...
						</span>
					) }
				</>
			) }

			{ /* Page numbers */ }
			{ pages.map( ( page ) => (
				<button
					key={ page }
					className={ `nb-px-3 nb-py-2.5 nb-rounded-lg nb-border nb-font-medium nb-transition-all nb-duration-200 nb-shadow-sm focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-slate-500 focus:nb-ring-offset-2 ${
						page === currentPage
							? 'nb-bg-slate-600 nb-text-white nb-border-slate-600 nb-shadow-md'
							: 'nb-border-slate-200 nb-bg-white nb-text-slate-700 hover:nb-bg-slate-50 hover:nb-border-slate-300'
					}` }
					onClick={ () => onPageChange( page ) }
					aria-current={ page === currentPage ? 'page' : undefined }
				>
					{ page }
				</button>
			) ) }

			{ /* Last page */ }
			{ endPage < totalPages && (
				<>
					{ endPage < totalPages - 1 && (
						<span className="nb-px-2 nb-py-2.5 nb-text-slate-400 nb-font-medium">
							...
						</span>
					) }
					<button
						className="nb-px-3 nb-py-2.5 nb-rounded-lg nb-border nb-border-slate-200 nb-bg-white nb-text-slate-700 nb-font-medium hover:nb-bg-slate-50 hover:nb-border-slate-300 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-slate-500 focus:nb-ring-offset-2 nb-transition-all nb-duration-200 nb-shadow-sm"
						onClick={ () => onPageChange( totalPages ) }
					>
						{ totalPages }
					</button>
				</>
			) }

			{ /* Next button */ }
			{ currentPage < totalPages && (
				<button
					className="nb-flex nb-items-center nb-gap-2 nb-px-4 nb-py-2.5 nb-rounded-lg nb-border nb-border-slate-200 nb-bg-white nb-text-slate-700 nb-font-medium hover:nb-bg-slate-50 hover:nb-border-slate-300 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-slate-500 focus:nb-ring-offset-2 nb-transition-all nb-duration-200 nb-shadow-sm"
					onClick={ () => onPageChange( currentPage + 1 ) }
					aria-label={ __( 'Go to next page', 'nemtly-booking' ) }
				>
					<span className="nb-hidden sm:nb-inline">
						{ __( 'Next', 'nemtly-booking' ) }
					</span>
					<svg
						className="nb-w-4 nb-h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={ 2 }
							d="M9 5l7 7-7 7"
						/>
					</svg>
				</button>
			) }

			{ /* Page info */ }
			<div className="nb-ml-6 nb-text-sm nb-text-slate-500 nb-hidden md:nb-block">
				{ __( 'Page', 'nemtly-booking' ) } { currentPage }{ ' ' }
				{ __( 'of', 'nemtly-booking' ) } { totalPages }
			</div>
		</div>
	);
};

/**
 * Initialize Events Display
 */
const initEventsDisplay = () => {
	const containers = document.querySelectorAll( '.nb-events-display' );

	containers.forEach( ( container ) => {
		const limit = parseInt( container.dataset.limit ) || 12;
		const showFilters = container.dataset.showFilters === 'true';
		const showPagination = container.dataset.showPagination === 'true';
		const showSearch = container.dataset.showSearch === 'true';
		const initialLayout = container.dataset.layout || 'grid';

		const root = ReactDOM.createRoot( container );
		root.render(
			<EventsDisplay
				limit={ limit }
				showFilters={ showFilters }
				showPagination={ showPagination }
				showSearch={ showSearch }
				initialLayout={ initialLayout }
			/>
		);
	} );
};

// Initialize when DOM is ready
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initEventsDisplay );
} else {
	initEventsDisplay();
}

export default EventsDisplay;
